import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  BookOpenIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  Squares2X2Icon,
  DocumentTextIcon,
  PlayIcon,
  StopIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { SearchableUserSelect } from '@/components/ui/SearchableUserSelect'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import { getUsers, User } from '@/api/usersApi'
import { getRoles } from '@/api/rolesApi'
import { getLessons, createLesson, updateLesson, deleteLesson, getLessonStats, Lesson, CreateLessonRequest, UpdateLessonRequest } from '@/api/lessonsApi'
import { getStreams, Stream } from '@/api/academicApi'
import { createSessionFromLesson, endSession, getSessions } from '@/api/sessionsApi'
import toast from 'react-hot-toast'

// Form schema for creating new lesson
const addLessonSchema = z.object({
  title: z.string().min(3, 'Lesson title must be at least 3 characters'),
  description: z.string().optional(),
  duration: z.number().min(1, 'Duration must be at least 1 minute').max(300, 'Duration cannot exceed 300 minutes'),
  teacherIds: z.array(z.string()).min(1, 'At least one teacher is required'),
  objectives: z.string().optional(),
  materials: z.string().optional(),
  selectedStreams: z.array(z.string()).min(1, 'At least one stream must be selected'),
})

type AddLessonData = z.infer<typeof addLessonSchema>

// Using imported Lesson and Stream interfaces from API


const LessonsPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { userRole } = useUserRole()
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null)
  const [showStartSessionModal, setShowStartSessionModal] = useState(false)
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')

  // Available streams and teachers for lesson creation
  const [availableStreams, setAvailableStreams] = useState<Stream[]>([])
  const [availableTeachers, setAvailableTeachers] = useState<User[]>([])
  const [loadingTeachers, setLoadingTeachers] = useState(false)

  // Track active sessions for lessons
  const [activeSessions, setActiveSessions] = useState<Map<string, any>>(new Map())
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    byTeacher: {} as Record<string, number>
  })

  const addLessonForm = useForm<AddLessonData>({
    resolver: zodResolver(addLessonSchema),
    defaultValues: {
      title: '',
      description: '',
      duration: 45, // Default 45 minutes
      teacherIds: [],
      objectives: '',
      materials: '',
      selectedStreams: [],
    }
  })

  // Edit form for updating lessons
  const editLessonForm = useForm<AddLessonData>({
    resolver: zodResolver(addLessonSchema),
    defaultValues: {
      title: '',
      description: '',
      duration: 45,
      teacherIds: [],
      objectives: '',
      materials: '',
      selectedStreams: [],
    }
  })

  // Debounce the search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  // Fetch lessons when debounced search changes
  useEffect(() => {
    fetchLessons()
  }, [debouncedSearchQuery])

  // Fetch streams, teachers, and active sessions on mount
  useEffect(() => {
    fetchStreams()
    fetchTeachers()
    fetchActiveSessions()
  }, [])

  // Set default teacher ID for teacher users
  useEffect(() => {
    if (userRole === 'teacher' && user?.id) {
      addLessonForm.setValue('teacherIds', [user.id])
    }
  }, [userRole, user?.id, addLessonForm])

  const fetchLessons = async () => {
    // Only show full loading on initial load
    if (lessons.length === 0) {
      setLoading(true)
    }
    try {
      console.log('Fetching lessons with search:', debouncedSearchQuery)

      // Fetch lessons from API with search
      const response = await getLessons({
        search: debouncedSearchQuery.trim() || undefined
      })

      console.log('Lessons response:', response)
      setLessons(response.data || [])

      // Fetch stats (only for admins)
      if (userRole !== 'teacher') {
        try {
          const statsResponse = await getLessonStats()
          console.log('Stats response:', statsResponse)

          setStats({
            total: statsResponse.data.totalLessons,
            byTeacher: statsResponse.data.byTeacher
          })
        } catch (error) {
          console.log('Could not fetch lesson stats (likely insufficient privileges)')
          setStats({
            total: response.data?.length || 0,
            byTeacher: {}
          })
        }
      } else {
        // For teachers, just use the lessons count
        setStats({
          total: response.data?.length || 0,
          byTeacher: {}
        })
      }

    } catch (error: any) {
      console.error('Failed to fetch lessons:', error)
      toast.error(error.response?.data?.message || 'Failed to load lessons')
      setLessons([])
      setStats({ total: 0, byTeacher: {} })
    } finally {
      setLoading(false)
    }
  }

  const fetchStreams = async () => {
    try {
      console.log('Fetching streams...')
      const streams = await getStreams()
      console.log('Fetched streams:', streams)
      setAvailableStreams(streams)

      if (streams.length === 0) {
        toast.error('No streams found. Please create streams first.')
      }
    } catch (error: any) {
      console.error('Failed to fetch streams:', error)
      toast.error(error.response?.data?.message || 'Failed to load streams')
      setAvailableStreams([])
    }
  }

  const fetchTeachers = async () => {
    setLoadingTeachers(true)
    try {
      console.log('Fetching teachers for lesson creation...')

      // For teachers, just use themselves as the only option
      if (userRole === 'teacher' && user) {
        console.log('Teacher user - using self as teacher option')
        setAvailableTeachers([user as User])
        setLoadingTeachers(false)
        return
      }

      // For admins, get all roles to find the Teacher role ID
      const rolesResponse = await getRoles()
      console.log('Available roles:', rolesResponse.data)

      const teacherRole = rolesResponse.data.find(role =>
        role.name.toLowerCase().includes('teacher')
      )

      if (teacherRole) {
        console.log('Found teacher role:', teacherRole)
        // Fetch teachers using the correct role ID
        const teachersResponse = await getUsers({
          roleId: teacherRole.id,
          status: 'active',
          limit: 100 // Get all active teachers
        })
        console.log('Fetched teachers:', teachersResponse.users)
        setAvailableTeachers(teachersResponse.users)

        if (teachersResponse.users.length === 0) {
          toast.error('No active teachers found in the system.')
        }
      } else {
        console.log('No teacher role found, fetching all users')
        // Fallback: get all users and filter by role name
        const allUsersResponse = await getUsers({ status: 'active', limit: 100 })
        const teachers = allUsersResponse.users.filter(user =>
          user.role.toLowerCase().includes('teacher')
        )
        console.log('Filtered teachers from all users:', teachers)
        setAvailableTeachers(teachers)

        if (teachers.length === 0) {
          toast.error('No teachers found in the system.')
        }
      }
    } catch (error) {
      console.error('Failed to fetch teachers:', error)
      setAvailableTeachers([])
      toast.error('Could not load teachers from server.')
    } finally {
      setLoadingTeachers(false)
    }
  }

  const handleAddLesson = async (lessonData: AddLessonData) => {
    try {
      console.log('Creating lesson:', lessonData)

      const createData: CreateLessonRequest = {
        title: lessonData.title,
        teacherIds: lessonData.teacherIds,
        description: lessonData.description,
        duration: lessonData.duration,
        objectives: lessonData.objectives,
        materials: lessonData.materials,
        selectedStreams: lessonData.selectedStreams
      }

      const response = await createLesson(createData)
      console.log('Lesson created:', response)

      toast.success(`Lesson "${response.data.title}" created successfully!`)
      setShowAddModal(false)
      addLessonForm.reset({
        title: '',
        description: '',
        duration: 45,
        teacherIds: userRole === 'teacher' && user?.id ? [user.id] : [],
        objectives: '',
        materials: '',
        selectedStreams: [],
      })
      fetchLessons() // Refresh the lessons list

    } catch (error: any) {
      console.error('Failed to create lesson:', error)
      toast.error(error.response?.data?.message || 'Failed to create lesson')
    }
  }

  const handleViewLesson = (lesson: Lesson) => {
    // Navigate to lesson detail page instead of showing modal
    navigate(`/lesson-detail/${lesson.id}`)
  }

  const handleEditLesson = (lesson: Lesson) => {
    setEditingLesson(lesson)
    // Get teacher IDs from teachers array or single teacher
    const teacherIds = lesson.teachers?.map(t => t.id) || (lesson.teacher?.id ? [lesson.teacher.id] : [])
    editLessonForm.reset({
      title: lesson.title,
      description: lesson.description || '',
      duration: lesson.duration,
      teacherIds: teacherIds,
      objectives: lesson.objectives || '',
      materials: lesson.materials || '',
      selectedStreams: lesson.streams.map(s => s.id),
    })
    setShowEditModal(true)
  }

  const handleSaveEdit = async (data: AddLessonData) => {
    if (!editingLesson) return

    try {
      const updateData: UpdateLessonRequest = {
        title: data.title,
        teacherIds: data.teacherIds,
        description: data.description,
        duration: data.duration,
        objectives: data.objectives,
        materials: data.materials,
        selectedStreams: data.selectedStreams,
      }

      await updateLesson(editingLesson.id || editingLesson._id || '', updateData)
      toast.success(`"${data.title}" updated successfully!`)
      setShowEditModal(false)
      setEditingLesson(null)
      editLessonForm.reset()
      fetchLessons()
    } catch (error: any) {
      console.error('Failed to update lesson:', error)
      const message = error?.response?.data?.message || 'Failed to update lesson'
      toast.error(message)
    }
  }

  const handleToggleStatus = async (lesson: Lesson) => {
    const newStatus = !(lesson.isActive ?? true)
    const statusText = newStatus ? 'activated' : 'deactivated'

    try {
      await updateLesson(lesson.id || lesson._id || '', {
        isActive: newStatus,
      })
      toast.success(`"${lesson.title}" ${statusText} successfully!`)
      fetchLessons()
    } catch (error: any) {
      console.error('Failed to toggle lesson status:', error)
      const message = error?.response?.data?.message || 'Failed to update lesson status'
      toast.error(message)
    }
  }

  const handleDeleteLesson = async (lesson: Lesson) => {
    if (confirm(`Are you sure you want to delete "${lesson.title}"?`)) {
      try {
        await deleteLesson(lesson._id)
        toast.success(`"${lesson.title}" deleted successfully`)
        fetchLessons() // Refresh the lessons list
      } catch (error: any) {
        console.error('Failed to delete lesson:', error)
        toast.error(error.response?.data?.message || 'Failed to delete lesson')
      }
    }
  }

  const fetchActiveSessions = async () => {
    try {
      // Fetch all active sessions to check which lessons have ongoing sessions
      const response = await getSessions({ status: 'in_progress' })
      const activeSessionMap = new Map()

      response.data.sessions.forEach((session: any) => {
        activeSessionMap.set(session.lessonId, session)
      })

      setActiveSessions(activeSessionMap)
    } catch (error) {
      console.error('Failed to fetch active sessions:', error)
    }
  }

  const handleStartLesson = (lesson: Lesson) => {
    setSelectedLesson(lesson)
    setShowStartSessionModal(true)
  }

  const handleCreateSession = async (teacherId: string) => {
    if (!selectedLesson) return

    setActionLoading(selectedLesson.id)
    try {
      const streamIds = selectedLesson.streams.map(stream => stream.id)

      console.log('Creating session with data:', {
        lessonId: selectedLesson.id,
        teacherId,
        streamIds,
        title: selectedLesson.title
      })

      await createSessionFromLesson(
        selectedLesson.id,
        teacherId,
        streamIds,
        selectedLesson.title,
        selectedLesson.description,
        selectedLesson.duration
      )
      toast.success('Session started successfully!')
      setShowStartSessionModal(false)
      setSelectedLesson(null)
      fetchActiveSessions() // Refresh active sessions
    } catch (error: any) {
      console.error('Failed to create and start session:', error)
      const message = error?.response?.data?.message || 'Failed to start session'
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleStopLesson = async (lesson: Lesson) => {
    const activeSession = activeSessions.get(lesson.id)
    if (!activeSession) return

    const notes = prompt('Enter session notes (optional):')
    if (notes === null) return // User cancelled

    setActionLoading(lesson.id)
    try {
      await endSession(activeSession.id, notes || undefined)
      toast.success('Session ended successfully!')
      fetchActiveSessions() // Refresh active sessions
    } catch (error: any) {
      console.error('Failed to end session:', error)
      const message = error?.response?.data?.message || 'Failed to end session'
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }


  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return `${hours}h ${mins}m`
    }
    return `${mins}m`
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="dashboard-stat-card animate-pulse">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="dashboard-card animate-pulse">
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BookOpenIcon className="h-8 w-8 text-indigo-600 mr-3" />
            Lessons Management
          </h1>
          <p className="text-gray-600 mt-1">
            Create and manage lessons across multiple streams
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Lesson
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Lessons</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <BookOpenIcon className="h-8 w-8 text-indigo-600" />
          </div>
        </div>

        {Object.entries(stats.byTeacher).slice(0, 3).map(([teacherName, count]) => (
          <div key={teacherName} className="dashboard-stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{teacherName}</p>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
              </div>
              <AcademicCapIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
        <form className="flex-1 relative max-w-md" onSubmit={(e) => e.preventDefault()}>
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search lessons and teachers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            className="form-input pl-10"
          />
        </form>

        <div className="flex items-center space-x-3">

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded text-sm ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Lessons Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lessons?.map((lesson) => (
            <motion.div
              key={lesson.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="dashboard-card hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleViewLesson(lesson)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <BookOpenIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{lesson.title}</h3>
                    <p className="text-sm text-gray-600">by {lesson.teacher.name}</p>
                  </div>
                </div>
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  Teacher
                </span>
              </div>

              <p className="text-sm text-gray-600 mb-4">{lesson.description}</p>

              {/* Lesson Details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Duration:</span>
                  <span className="text-gray-900 font-medium">{formatDuration(lesson.duration)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Streams:</span>
                  <span className="text-gray-900 font-medium">{lesson.streams.length}</span>
                </div>
              </div>

              {/* Streams Tags */}
              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {lesson.streams.slice(0, 3).map((stream) => (
                    <span
                      key={stream.id}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                    >
                      {stream.name}
                    </span>
                  ))}
                  {lesson.streams.length > 3 && (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                      +{lesson.streams.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-xs text-gray-500">
                  Created by <span className="font-medium text-gray-900">{lesson.createdBy}</span>
                </span>
                <div className="flex space-x-2">
                  {/* Start/Stop Session Button */}
                  {activeSessions.has(lesson.id) ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleStopLesson(lesson)
                      }}
                      className="btn btn-sm bg-red-600 text-white hover:bg-red-700 text-xs"
                      title="Stop Active Session"
                      disabled={actionLoading === lesson.id}
                    >
                      {actionLoading === lesson.id ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                      ) : (
                        <StopIcon className="h-3 w-3 mr-1" />
                      )}
                      {actionLoading === lesson.id ? 'Stopping...' : 'Stop'}
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleStartLesson(lesson)
                      }}
                      className="btn btn-sm bg-green-600 text-white hover:bg-green-700 text-xs"
                      title="Start Session"
                      disabled={actionLoading === lesson.id}
                    >
                      {actionLoading === lesson.id ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                      ) : (
                        <PlayIcon className="h-3 w-3 mr-1" />
                      )}
                      {actionLoading === lesson.id ? 'Starting...' : 'Start'}
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditLesson(lesson)
                    }}
                    className="btn btn-ghost text-xs"
                    title="Edit Lesson"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggleStatus(lesson)
                    }}
                    className={`btn btn-ghost text-xs ${(lesson.isActive ?? true) ? 'text-orange-600' : 'text-green-600'}`}
                    title={(lesson.isActive ?? true) ? 'Deactivate' : 'Activate'}
                  >
                    {(lesson.isActive ?? true) ? (
                      <XCircleIcon className="h-4 w-4" />
                    ) : (
                      <CheckCircleIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteLesson(lesson)
                    }}
                    className="btn btn-ghost text-xs text-red-600"
                    title="Delete Lesson"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Lessons Table View */}
      {viewMode === 'table' && (
        <div className="dashboard-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lesson
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teacher
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Streams
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lessons.map((lesson) => (
                  <motion.tr
                    key={lesson.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewLesson(lesson)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                          <BookOpenIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{lesson.title}</div>
                          <div className="text-sm text-gray-500">{lesson.description || 'No description'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{lesson.teacher.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDuration(lesson.duration)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {lesson.streams.slice(0, 2).map((stream) => (
                          <span
                            key={stream.id}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                          >
                            {stream.name}
                          </span>
                        ))}
                        {lesson.streams.length > 2 && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                            +{lesson.streams.length - 2} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{lesson.createdBy}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        {/* Start/Stop Session Button */}
                        {activeSessions.has(lesson.id) ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleStopLesson(lesson)
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Stop Active Session"
                            disabled={actionLoading === lesson.id}
                          >
                            {actionLoading === lesson.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <StopIcon className="h-4 w-4" />
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleStartLesson(lesson)
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Start Session"
                            disabled={actionLoading === lesson.id}
                          >
                            {actionLoading === lesson.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                            ) : (
                              <PlayIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditLesson(lesson)
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit Lesson"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToggleStatus(lesson)
                          }}
                          className={(lesson.isActive ?? true) ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                          title={(lesson.isActive ?? true) ? 'Deactivate' : 'Activate'}
                        >
                          {(lesson.isActive ?? true) ? (
                            <XCircleIcon className="h-4 w-4" />
                          ) : (
                            <CheckCircleIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteLesson(lesson)
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Lesson"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {lessons.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No lessons found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery
                ? 'Try adjusting your search'
                : 'Get started by creating your first lesson'
              }
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create First Lesson
            </button>
          </div>
        </motion.div>
      )}

      {/* Add Lesson Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Create New Lesson</h3>
                    <p className="text-sm text-gray-500">Design a lesson and assign it to streams</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addLessonForm.handleSubmit(handleAddLesson)} className="p-6 space-y-6" autoComplete="off">
                {/* Hidden fields to prevent autofill */}
                <input type="text" style={{display:'none'}} autoComplete="off" />
                <input type="email" style={{display:'none'}} autoComplete="off" />
                <input type="password" style={{display:'none'}} autoComplete="off" />

                {/* Lesson Basic Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <BookOpenIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    Lesson Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addLessonForm.register('title')}
                      label="Lesson Title"
                      placeholder="e.g., Introduction to Fractions"
                      autoComplete="off"
                      data-lpignore="true"
                      error={addLessonForm.formState.errors.title?.message}
                    />

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign Teachers *
                      </label>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                        {loadingTeachers ? (
                          <div className="text-center py-4 text-gray-500">Loading teachers...</div>
                        ) : availableTeachers.length === 0 ? (
                          <div className="text-center py-4 text-gray-500">No teachers available</div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {availableTeachers.map((teacher) => (
                              <label
                                key={teacher.id || teacher._id}
                                className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-white cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  value={teacher.id || teacher._id}
                                  checked={addLessonForm.watch('teacherIds')?.includes(teacher.id || teacher._id)}
                                  onChange={(e) => {
                                    const currentIds = addLessonForm.getValues('teacherIds') || []
                                    const teacherId = teacher.id || teacher._id
                                    if (e.target.checked) {
                                      addLessonForm.setValue('teacherIds', [...currentIds, teacherId])
                                    } else {
                                      addLessonForm.setValue('teacherIds', currentIds.filter(id => id !== teacherId))
                                    }
                                  }}
                                  className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300"
                                />
                                <div className="flex items-center space-x-2">
                                  <UserIcon className="h-4 w-4 text-indigo-600" />
                                  <span className="text-sm font-medium text-gray-900">{teacher.fullName || `${teacher.firstName} ${teacher.lastName}`}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      {addLessonForm.formState.errors.teacherIds && (
                        <p className="mt-1 text-sm text-red-600">{addLessonForm.formState.errors.teacherIds.message}</p>
                      )}
                    </div>

                    <Input
                      {...addLessonForm.register('duration', { valueAsNumber: true })}
                      type="number"
                      label="Duration (Minutes)"
                      placeholder="45"
                      min="5"
                      max="300"
                      autoComplete="off"
                      error={addLessonForm.formState.errors.duration?.message}
                    />

                    <div className="md:col-span-2">
                      <Input
                        {...addLessonForm.register('description')}
                        label="Description (Optional)"
                        placeholder="Brief description of the lesson content"
                        autoComplete="off"
                        data-lpignore="true"
                        error={addLessonForm.formState.errors.description?.message}
                      />
                    </div>
                  </div>
                </div>

                {/* Stream Selection */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <Squares2X2Icon className="h-5 w-5 text-blue-600 mr-2" />
                    Assign to Streams
                  </h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Multi-Stream Assignment:</strong> Select one or more streams where this lesson will be taught.
                      The same lesson can be used across multiple streams.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4">
                    {availableStreams.map((stream) => (
                      <label
                        key={stream.id}
                        className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          value={stream.id}
                          {...addLessonForm.register('selectedStreams')}
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                        />
                        <div className="flex items-center space-x-2">
                          <Squares2X2Icon className="h-4 w-4 text-blue-600" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{stream.name}</div>
                            <div className="text-xs text-gray-500">{stream.gradeName || 'Grade'} • {stream.currentStudents || 0} students</div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {addLessonForm.formState.errors.selectedStreams && (
                    <p className="mt-2 text-sm text-red-600">{addLessonForm.formState.errors.selectedStreams.message}</p>
                  )}
                </div>

                {/* Lesson Content */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-green-600 mr-2" />
                    Lesson Content (Optional)
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Learning Objectives
                      </label>
                      <textarea
                        {...addLessonForm.register('objectives')}
                        rows={3}
                        className="form-input"
                        placeholder="What will students learn from this lesson?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Materials & Resources
                      </label>
                      <textarea
                        {...addLessonForm.register('materials')}
                        rows={3}
                        className="form-input"
                        placeholder="List required materials, textbooks, equipment, etc."
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-green-900">Lesson Creation Process</h5>
                      <div className="text-sm text-green-700 mt-1 space-y-1">
                        <p>• Lesson will be created and assigned to selected streams</p>
                        <p>• Teachers can access and use this lesson in their classes</p>
                        <p>• Students in assigned streams will see this lesson in their schedule</p>
                        <p>• Lesson materials and objectives will be available to all assigned streams</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <BookOpenIcon className="h-4 w-4 mr-2" />
                    Create Lesson
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Start Session Modal */}
      <AnimatePresence>
        {showStartSessionModal && selectedLesson && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full"
            >
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-medium text-gray-900">Start Session</h3>
                <button
                  onClick={() => {
                    setShowStartSessionModal(false)
                    setSelectedLesson(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Lesson: {selectedLesson.title}</h4>
                  <p className="text-sm text-gray-600">Duration: {formatDuration(selectedLesson.duration)}</p>
                  <p className="text-sm text-gray-600">
                    Streams: {selectedLesson.streams.map(s => s.name).join(', ')}
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Teacher to conduct session:
                  </label>

                  {user?.role === 'TEACHER' ? (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-blue-600 mr-2" />
                        <span className="text-sm font-medium text-blue-800">
                          {user.firstName} {user.lastName}
                        </span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">You will conduct this session</p>
                    </div>
                  ) : (
                    <div id="teacherSelectContainer">
                      <SearchableUserSelect
                        placeholder="Search for teacher to conduct session..."
                        value={selectedLesson.teacher.id}
                        onChange={(teacherId) => {
                          // Store the selected teacher ID for later use
                          if (teacherId) {
                            (window as any).selectedTeacherId = teacherId
                          }
                        }}
                        roleFilter="teacher"
                        className="w-full"
                      />
                    </div>
                  )}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> This will create a new session starting immediately
                    and ending after {formatDuration(selectedLesson.duration)}.
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => {
                      setShowStartSessionModal(false)
                      setSelectedLesson(null)
                    }}
variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      const teacherId = user?.role === 'TEACHER'
                        ? user.id
                        : (window as any).selectedTeacherId || selectedLesson.teacher.id
                      handleCreateSession(teacherId)
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Start Session
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Lesson Modal */}
      <AnimatePresence>
        {showEditModal && editingLesson && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowEditModal(false)
              setEditingLesson(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Lesson</h3>
                    <p className="text-sm text-gray-500">Update lesson information</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingLesson(null)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={editLessonForm.handleSubmit(handleSaveEdit)} className="p-6 space-y-6" autoComplete="off">
                <input type="text" style={{display:'none'}} autoComplete="off" />

                {/* Lesson Basic Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <BookOpenIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    Lesson Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...editLessonForm.register('title')}
                      label="Lesson Title"
                      placeholder="e.g., Introduction to Fractions"
                      autoComplete="off"
                      data-lpignore="true"
                      error={editLessonForm.formState.errors.title?.message}
                    />

                    <Input
                      {...editLessonForm.register('duration', { valueAsNumber: true })}
                      type="number"
                      label="Duration (Minutes)"
                      placeholder="45"
                      min="5"
                      max="300"
                      autoComplete="off"
                      error={editLessonForm.formState.errors.duration?.message}
                    />

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign Teachers *
                      </label>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                        {loadingTeachers ? (
                          <div className="text-center py-4 text-gray-500">Loading teachers...</div>
                        ) : availableTeachers.length === 0 ? (
                          <div className="text-center py-4 text-gray-500">No teachers available</div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {availableTeachers.map((teacher) => (
                              <label
                                key={teacher.id || teacher._id}
                                className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-white cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  value={teacher.id || teacher._id}
                                  checked={editLessonForm.watch('teacherIds')?.includes(teacher.id || teacher._id)}
                                  onChange={(e) => {
                                    const currentIds = editLessonForm.getValues('teacherIds') || []
                                    const teacherId = teacher.id || teacher._id
                                    if (e.target.checked) {
                                      editLessonForm.setValue('teacherIds', [...currentIds, teacherId])
                                    } else {
                                      editLessonForm.setValue('teacherIds', currentIds.filter(id => id !== teacherId))
                                    }
                                  }}
                                  className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300"
                                />
                                <div className="flex items-center space-x-2">
                                  <UserIcon className="h-4 w-4 text-indigo-600" />
                                  <span className="text-sm font-medium text-gray-900">{teacher.fullName || `${teacher.firstName} ${teacher.lastName}`}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      {editLessonForm.formState.errors.teacherIds && (
                        <p className="mt-1 text-sm text-red-600">{editLessonForm.formState.errors.teacherIds.message}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <Input
                        {...editLessonForm.register('description')}
                        label="Description (Optional)"
                        placeholder="Brief description of the lesson content"
                        autoComplete="off"
                        data-lpignore="true"
                        error={editLessonForm.formState.errors.description?.message}
                      />
                    </div>
                  </div>
                </div>

                {/* Stream Selection */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <Squares2X2Icon className="h-5 w-5 text-blue-600 mr-2" />
                    Assigned Streams
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-4">
                    {availableStreams.map((stream) => (
                      <label
                        key={stream.id}
                        className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          value={stream.id}
                          checked={editLessonForm.watch('selectedStreams')?.includes(stream.id)}
                          onChange={(e) => {
                            const currentStreams = editLessonForm.getValues('selectedStreams') || []
                            if (e.target.checked) {
                              editLessonForm.setValue('selectedStreams', [...currentStreams, stream.id])
                            } else {
                              editLessonForm.setValue('selectedStreams', currentStreams.filter(id => id !== stream.id))
                            }
                          }}
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                        />
                        <div className="flex items-center space-x-2">
                          <Squares2X2Icon className="h-4 w-4 text-blue-600" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{stream.name}</div>
                            <div className="text-xs text-gray-500">{stream.gradeName || 'Grade'}</div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {editLessonForm.formState.errors.selectedStreams && (
                    <p className="mt-2 text-sm text-red-600">{editLessonForm.formState.errors.selectedStreams.message}</p>
                  )}
                </div>

                {/* Lesson Content */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-green-600 mr-2" />
                    Lesson Content (Optional)
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Learning Objectives
                      </label>
                      <textarea
                        {...editLessonForm.register('objectives')}
                        rows={3}
                        className="form-input"
                        placeholder="What will students learn from this lesson?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Materials & Resources
                      </label>
                      <textarea
                        {...editLessonForm.register('materials')}
                        rows={3}
                        className="form-input"
                        placeholder="List required materials, textbooks, equipment, etc."
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingLesson(null)
                    }}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default LessonsPage
